import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from "@bednbody/ui/components/combobox";
import { Button } from "@bednbody/ui/components/button";
import { NativeSelect, NativeSelectOption } from "@bednbody/ui/components/native-select";
import { cn } from "@bednbody/ui/lib/utils";

/** The columns a hub picker needs. Not the row type — this never reads `region`. */
export interface HubOption {
  id: string;
  name: string;
  is_pilot?: boolean | null;
}

/**
 * `__all__` can never be a real hub id (those are uuids), so the sentinel needs
 * no collision check. It is an OBJECT rather than a bare id because the
 * Combobox matches items by identity, and a string would also match a hub whose
 * id happened to be the same string.
 */
const ALL = { id: "__all__", name: "All hubs", is_pilot: false } satisfies HubOption;

/** One spelling of the pilot marker, for the trigger and the list alike. */
function hubLabel(hub: HubOption): string {
  return hub.is_pilot ? `${hub.name} · pilot` : hub.name;
}

interface HubSelectProps {
  hubs: readonly HubOption[];
  /** The chosen hub id. `null`, `undefined` and `""` all mean "no specific hub". */
  value: string | null | undefined;
  onValueChange: (hubId: string | null) => void;
  /**
   * `select` for a handful of hubs; `combobox` to search a long list. The
   * combobox is the one that needs truncating — its trigger is a fixed-width
   * Button carrying `whitespace-nowrap`, so a long hub name paints outside it
   * unless the value has an element of its own to shrink on.
   */
  variant?: "select" | "combobox";
  /** Wording for the "no specific hub" option. */
  allLabel?: string;
  /**
   * Combobox trigger text before anything is chosen. Defaults to `allLabel`;
   * set it when the prompt is not the same as the option it leads to — a
   * required field says "Pick a hub…" but its first row still reads
   * "No hub (office)".
   */
  placeholder?: string;
  /** Drop that option entirely — a dialog that must pick exactly one hub. */
  allowAll?: boolean;
  /** Accessible name. Supply when there is no visible label pointing at this. */
  ariaLabel?: string;
  id?: string;
  className?: string;
  /** `select` only; the combobox trigger is a Button and has no such size. */
  size?: "sm" | "default";
  /**
   * Hubs to offer but grey out — e.g. the one a person is already assigned to.
   * They stay VISIBLE on purpose: a reassign dialog that hides where someone
   * currently is removes the context you need to pick a different one.
   */
  disableHubIds?: readonly string[];
}

/**
 * The one hub picker. Every "which hub" control in admin is this, so they all
 * search the same way, mark the pilot hub the same way, and stay inside their
 * own width instead of spilling over it.
 */
export function HubSelect({
  hubs,
  value,
  onValueChange,
  variant = "select",
  allLabel = "All hubs",
  placeholder,
  allowAll = true,
  ariaLabel,
  id,
  className,
  size,
  disableHubIds,
}: HubSelectProps) {
  const offered = hubs;
  const isDisabled = (hub: HubOption) => disableHubIds?.includes(hub.id) === true;
  const isAll = value == null || value === "";

  if (variant === "select") {
    return (
      <NativeSelect
        aria-label={ariaLabel}
        className={className}
        id={id}
        size={size}
        value={isAll ? "" : value}
        onChange={(event) => onValueChange(event.target.value || null)}
      >
        {allowAll ? <NativeSelectOption value="">{allLabel}</NativeSelectOption> : null}
        {offered.map((hub) => (
          <NativeSelectOption disabled={isDisabled(hub)} key={hub.id} value={hub.id}>
            {hubLabel(hub)}
          </NativeSelectOption>
        ))}
      </NativeSelect>
    );
  }

  const allOption = { ...ALL, name: allLabel };
  const items = allowAll ? [allOption, ...offered] : offered;
  const found =
    value == null || value === "" ? null : (offered.find((hub) => hub.id === value) ?? null);
  // Without `allowAll` there is no sentinel, so "nothing chosen" stays null
  // rather than silently resolving to the all-hubs row.
  const selected = allowAll ? (found ?? allOption) : found;

  return (
    <Combobox
      items={items}
      value={selected}
      onValueChange={(hub) => {
        if (!hub) {
          onValueChange(null);
          return;
        }
        onValueChange(allowAll && hub.id === ALL.id ? null : hub.id);
      }}
      itemToStringLabel={hubLabel}
    >
      <ComboboxTrigger
        render={
          <Button
            aria-label={ariaLabel}
            id={id}
            variant="secondary"
            className={cn("w-48 justify-between font-normal text-left", className)}
          />
        }
      >
        {/* Button is `whitespace-nowrap` + `shrink-0` and ComboboxValue renders
            no element, so this span is what actually ellipsises the label. */}
        <span className="min-w-0 truncate">
          <ComboboxValue placeholder={placeholder ?? allLabel} />
        </span>
      </ComboboxTrigger>
      <ComboboxContent>
        <ComboboxInput placeholder="Search hubs…" showTrigger={false} />
        <ComboboxEmpty>No hubs found.</ComboboxEmpty>
        <ComboboxList>
          {(hub) => (
            <ComboboxItem disabled={isDisabled(hub)} key={hub.id} value={hub}>
              {hubLabel(hub)}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
